import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,  
    CircularProgress, 
    Tooltip 
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { formatNumber } from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const ProductSales = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);      

    const [productSales, setProductSales] = useState([]);
    
    useEffect(() => {        
        const handleProductSales = async () => {
            if (!startDate || !endDate) return [];

            try {           
                setError(null);
                setLoading(true);
                
                const url = '/inflightsales/productsales';
                const payload = { startDate, endDate, timeMode };
                const res = await api.post(url, payload);
               
                setProductSales(res.data);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }; 
        
        handleProductSales();      
    }, [timeMode, startDate, endDate]); 

    const handleExcelJSDownload = () => {
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14); // YYYYMMDDHHMMSS
        const fileName = `ProductSales_${timestamp}.xlsx`;
        
        if (!productSales || productSales.length === 0) {
            toast.error("No data available to export.");
            return;
        }

        exportToExcel(productSales, fileName);
    };

    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            headerClassName: 'theme-column--header',
            filterable: false,      
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },
        {
            field: "productName",
            headerName: "ProductName",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 300 } : { flex: 5 };
            })(),
        },
        {
            field: "category",
            headerName: "Category",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.2 };
            })(),
        },
        {
            field: "unit",
            headerName: "Unit",
            headerClassName: 'theme-column--header',
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.2 };
            })(),
        },
        {
            field: "preorderQuantity",
            headerName: "PreorderQuantity",
            headerClassName: 'theme-column--header',
            align: 'right',
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },
        {
            field: "directQuantity",
            headerName: "DirectQuantity",
            headerClassName: 'theme-column--header',
            align: 'right',
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },  
        {
            field: "totalQuantity",
            headerName: "TotalQuantity",
            headerClassName: 'theme-column--header',
            align: 'right',
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },  
        {
            field: "totalAmount",
            headerName: "TotalAmount",
            headerClassName: 'theme-column--header',
            align: 'right',
            valueFormatter: (params) => {
                if (!params) return ''; 
                const roundedValue = Math.ceil(params); 
                return roundedValue.toLocaleString(); 
            },
            ...(() => {
                return isMobile ? { width: 135 } : { flex: 2 };
            })(),
        },      
    ], [isMobile]);
    
    return (
        <Box className="main-container-box">            
            <FlexBetween>
                <Header 
                    title="Product Sales" 
                    subtitle={`${loading ? 'Loading...' : 'List of Product Sales'}`}
                /> 
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>  
                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton 
                                    onClick={() => {
                                        handleExcelJSDownload();
                                        logClientAction("EXPORT_REPORT", { fileName: "ProductSales", format: "Excel", startDate, endDate });
                                    }}  
                                >
                                    <DownloadIcon />
                                </StyledButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </FlexBetween>              
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12" }}
                    >
                        {isMobile ? (
                            <CardList
                                rows={productSales}
                                primaryField="productName"
                                minWidth={80}
                                fields={[
                                    { label: "Category",           key: "category" },
                                    { label: "Unit",               key: "unit" },
                                    { label: "Preorder Qty",       key: "preorderQuantity", cols: 2, render: (r) => formatNumber(r.preorderQuantity) },
                                    { label: "Direct Qty",         key: "directQuantity",   cols: 2, render: (r) => formatNumber(r.directQuantity) },
                                    { label: "Total Qty",          key: "totalQuantity",    cols: 2, render: (r) => formatNumber(r.totalQuantity) },
                                    { label: "Total Amount",       key: "totalAmount",      cols: 2, render: (r) => formatNumber(r.totalAmount) },
                                ]}
                                isActiveField={null}
                                canEdit={false}
                                canDelete={false}
                            />
                        ) : (
                            <DataGridComponent
                                rows={productSales}
                                columns={columns}
                                loading={loading}
                            />
                        )}
                    </Box>              
                </Box>
            )}
        </Box>
    );
};

export default ProductSales;
